import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextField from '../../../../components/molecules/TextField';
import {
    IN_NOT_IN,
    LESS_THAN_GREATER_THAN,
    MISSING_OPERATOR,
    validateCustomFieldName,
    validateNumber,
    validateText,
} from '../../constants';
import { getConditionType } from '../../utils';
import { ConditionOptionsShape } from '../../propShapes';
import DimensionSelect from './DimensionSelect';
import OperatorSelect from './OperatorSelect';
import CustomMultiValueInput from './CustomMultiValueInput';
import MultiValueSelect from './MultiValueSelect';
import { filterSpecialChars } from './conditionUtils';
import css from './style.css';

const TRANSLATION_KEY = 'STATIC.PAGES.FRAUD.CUSTOM_RULES';

const Condition = ({
    dimension,
    operator,
    customFieldName = '',
    values = null,
    first = false,
    only = false,
    disabled = false,
    invalid = false,
    conditionOptions = {},
    translate,
    editCondition,
    removeCondition,
}) => {
    const type = getConditionType(dimension);
    const deletable = !disabled && !only;
    const isExpandedOperator = IN_NOT_IN.map(op => op.value).includes(operator);
    const isNumberOperator = LESS_THAN_GREATER_THAN.map(op => op.value).includes(operator);
    const isCustomParameter = dimension === 'custom_parameter';
    const options = (type.optionsKey && conditionOptions[type.optionsKey]) || [];

    const scalarValue = typeof values === 'string' || typeof values === 'number' ? values : '';

    const showNumberInput =
        (type.number && operator !== MISSING_OPERATOR.value) || (isCustomParameter && isNumberOperator);
    const showTextInput = type.text && operator !== MISSING_OPERATOR.value && !isCustomParameter;

    const resolveUnits = typeof type.units === 'function' ? type.units : () => type.units;
    const unitsKey = showNumberInput ? resolveUnits(operator) : '';

    const handleNumberChange = value => {
        let next = typeof value === 'string' ? value.trim() : value;
        if (type.parseInt && next !== '' && !Number.isNaN(+next)) {
            next = +next;
        }
        editCondition('values', next);
    };

    const isTextValueInvalid = type.validate ? !type.validate({ values: scalarValue }) : !validateText(scalarValue);

    return (
        <div className={css.condition}>
            <div className={css.and}>{first ? '' : translate(`${TRANSLATION_KEY}.AND`)}</div>

            <DimensionSelect
                dimension={dimension}
                disabled={disabled}
                betaHiddenFlags={conditionOptions.betaHiddenFlags}
                translate={translate}
                editCondition={editCondition}
            />

            {isCustomParameter && (
                <TextField
                    type="text"
                    containerClass={classNames(css.conditionField, css.customFieldNameField)}
                    errorClassName={css.fieldError}
                    placeholder={`${TRANSLATION_KEY}.CUSTOM_FIELD_PLACEHOLDER`}
                    value={customFieldName}
                    disabled={disabled}
                    onChange={value => editCondition('customFieldName', filterSpecialChars(value, false))}
                    error={
                        invalid && !validateCustomFieldName(customFieldName)
                            ? `${TRANSLATION_KEY}.CUSTOM_FIELD_NAME_INVALID`
                            : null
                    }
                />
            )}

            {type.operators.length > 0 && (
                <OperatorSelect
                    operator={operator}
                    operators={type.operators}
                    disabled={disabled}
                    translate={translate}
                    editCondition={editCondition}
                />
            )}

            {showNumberInput && (
                <div className={css.numberInputContainer}>
                    <TextField
                        type="number"
                        containerClass={classNames(css.conditionField, css.numberField)}
                        errorClassName={css.fieldError}
                        value={scalarValue}
                        disabled={disabled}
                        onChange={handleNumberChange}
                        error={invalid && !validateNumber(scalarValue) ? `${TRANSLATION_KEY}.NUMBER_INVALID` : null}
                    />
                    {Boolean(unitsKey) && <span className={css.units}>{translate(unitsKey)}</span>}
                </div>
            )}

            {showTextInput && (
                <TextField
                    type="text"
                    containerClass={classNames(css.conditionField, css.textField)}
                    errorClassName={css.fieldError}
                    inputConfig={{ placeholder: type.placeholder }}
                    value={scalarValue}
                    disabled={disabled}
                    onChange={value => editCondition('values', value)}
                    error={invalid && isTextValueInvalid ? type.validationTextKey : null}
                />
            )}

            {isCustomParameter && isExpandedOperator && (
                <CustomMultiValueInput
                    values={values}
                    invalid={invalid}
                    disabled={disabled}
                    translate={translate}
                    editCondition={editCondition}
                />
            )}

            {(type.options || type.optionsKey || type.creatable) && !isCustomParameter && (
                <MultiValueSelect
                    values={values}
                    multi={!!type.multi}
                    options={options}
                    isCreatable={!!type.creatable}
                    invalid={invalid}
                    disabled={disabled}
                    editCondition={editCondition}
                />
            )}

            {deletable && (
                <button
                    type="button"
                    className={css.buttonRemove}
                    title={translate(`${TRANSLATION_KEY}.REMOVE_CONDITION`)}
                    onClick={removeCondition}
                >
                    ×
                </button>
            )}
        </div>
    );
};

Condition.propTypes = {
    dimension: PropTypes.string.isRequired,
    operator: PropTypes.string,
    customFieldName: PropTypes.string,
    values: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.string)]),
    first: PropTypes.bool,
    only: PropTypes.bool,
    invalid: PropTypes.bool,
    disabled: PropTypes.bool,
    conditionOptions: ConditionOptionsShape,
    translate: PropTypes.func.isRequired,
    editCondition: PropTypes.func.isRequired,
    removeCondition: PropTypes.func.isRequired,
};

export default Condition;
